import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    Box,
    Typography,
    TextField,
    Button,
    MenuItem,
    Modal,
} from "@mui/material";

const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 500,
    bgcolor: "#16213E",
    borderRadius: "20px",
    boxShadow: 24,
    p: 4,
    color: "#EFFFFD",
    textAlign: "center",
};

const keypadButtonStyle = {
    width: "80px",
    height: "80px",
    borderRadius: "50%",
    fontSize: "36px",
    fontWeight: "bold",
    backgroundColor: "#1A1A2E",
    color: "#FFFFFF",
    border: "2px solid #FFFFFF",
    margin: "8px",
    cursor: "pointer",
};

const StudentMeals = () => {
    // State
    const [devices, setDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState(
        localStorage.getItem("selectedDevice") || ""
    );
    const [selectedDeviceInfo, setSelectedDeviceInfo] = useState(null);

    const [studentNumber, setStudentNumber] = useState("");
    const [student, setStudent] = useState(null);

    const [mealService, setMealService] = useState("");
    const [error, setError] = useState(null);

    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [confirmationData, setConfirmationData] = useState({});
    const [studentAllergens, setStudentAllergens] = useState([]);

    // Fetch Devices
    useEffect(() => {
        const fetchDevices = async () => {
            try {
                const response = await axios.get(
                    "https://figazuresql.swancreate.com/api/swa/Devices"
                );
                setDevices(response.data || []);
                if (selectedDeviceId) {
                    const found = response.data.find((d) => d.apiid === selectedDeviceId);
                    if (found) setSelectedDeviceInfo(found);
                    else {
                        setSelectedDeviceId("");
                        localStorage.removeItem("selectedDevice");
                    }
                }
            } catch (err) {
                console.error(err);
                setError("Error fetching devices.");
            }
        };
        fetchDevices();
    }, [selectedDeviceId]);

    // Handle device change
    const handleDeviceChange = (e) => {
        const id = e.target.value;
        setSelectedDeviceId(id);
        localStorage.setItem("selectedDevice", id);
        setSelectedDeviceInfo(devices.find((d) => d.apiid === id) || null);
        setError(null);
    };

    // Keypad
    const handleKeypadDigit = (d) => setStudentNumber((p) => p + d);
    const handleBackspace = () =>
        setStudentNumber((p) => (p.length ? p.slice(0, -1) : ""));
    const handleClear = () => setStudentNumber("");

    // Fetch allergens for a student
    const fetchStudentAllergens = async (studentGuidID) => {
        try {
            const resp = await axios.get(
                "https://figazuresql.swancreate.com/api/swa/AllergenTracker"
            );
            const all = resp.data || [];
            setStudentAllergens(
                all.filter(
                    (item) =>
                        item.StudentguidID &&
                        studentGuidID &&
                        item.StudentguidID.toLowerCase() === studentGuidID.toLowerCase()
                )
            );
        } catch (err) {
            console.error(err);
        }
    };

    // Lookup student by ID number
    const handleStudentLookup = async (override) => {
        if (!selectedDeviceInfo) return setError("Please select a device.");
        if (!mealService) return setError("Please select a meal service.");

        let raw = (override ?? studentNumber).trim();
        if (!raw) return;
        setError(null);

        // strip suffix if non-numeric
        const last = raw.slice(-1);
        let suffix = "";
        if (isNaN(Number(last))) {
            suffix = last;
            raw = raw.slice(0, -1);
            if (
                selectedDeviceInfo.Suffix &&
                suffix !== selectedDeviceInfo.Suffix
            ) {
                setStudentNumber("");
                return setError(
                    `Suffix mismatch: expected '${selectedDeviceInfo.Suffix}'`
                );
            }
            setStudentNumber(raw);
        }

        try {
            const resp = await axios.get(
                "https://figazuresql.swancreate.com/api/swa/Students"
            );
            const found = (resp.data || []).find(
                (s) =>
                    s.StudentIDNumber &&
                    s.StudentIDNumber.toLowerCase() === raw.toLowerCase()
            );
            if (!found) {
                setStudent(null);
                return setError("Student not found.");
            }
            setStudent(found);
            fetchStudentAllergens(found.StudentguidID);
        } catch (err) {
            console.error(err);
            setError("Error fetching student data.");
        }
    };

    // auto-claim when ready
    useEffect(() => {
        if (student && selectedDeviceInfo && mealService) {
            handleClaimMeal();
        }
    }, [student, selectedDeviceInfo, mealService]);

    // Claim meal
    const handleClaimMeal = async () => {
        if (!student || !selectedDeviceInfo || !mealService) return;
        setError(null);
        try {
            const payload = {
                studentid: student.apiid,
                StudentIDNumber: student.StudentIDNumber,
                DeviceID: selectedDeviceInfo.apiid,
                SchoolId: student.SiteID,
                LocationName: selectedDeviceInfo.LocationName || "",
                FirstName: student.FirstName,
                LastName: student.LastName,
                MealService: mealService,
                PaymentStatus: student.PaymentStatus,
                isDC: student.isDc || false,
                isActive: 1,
            };
            await axios.post(
                "https://figazuresql.swancreate.com/api/swa/POS_MealCounts",
                payload
            );

            setConfirmationData({
                studentName: `${student.FirstName} ${student.LastName}`,
                deviceName: selectedDeviceInfo.DeviceName,
                schoolName: selectedDeviceInfo.SchoolName || "",
                locationName: selectedDeviceInfo.LocationName || "",
                mealService,
                time: new Date(),
                imageUrl: student.ImageURL || null,
            });
            setConfirmationOpen(true);

            setTimeout(closeConfirmation, 2000);
        } catch (err) {
            console.error(err);
            setError("Failed to claim meal.");
        }
    };

    const closeConfirmation = () => {
        setConfirmationOpen(false);
        setStudent(null);
        setStudentNumber("");
        setStudentAllergens([]);
    };

    // Render
    const validId = devices.some((d) => d.apiid === selectedDeviceId)
        ? selectedDeviceId
        : "";

    return (
        <Box
            sx={{
                backgroundColor: "#1A1A2E",
                color: "#EFFFFD",
                minHeight: "100vh",
                p: 4,
                borderRadius: 2,
                textAlign: "center",
            }}
        >
            <Typography variant="h4" sx={{ mb: 2, color: "#FFD700" }}>
                Select Device
            </Typography>
            <TextField
                select
                label="Devices"
                value={validId}
                onChange={handleDeviceChange}
                sx={{ mb: 3, width: 300 }}
            >
                <MenuItem value="">-- Select --</MenuItem>
                {devices.map((d) => (
                    <MenuItem key={d.apiid} value={d.apiid}>
                        {d.DeviceName} {d.SchoolName && `- ${d.SchoolName}`}
                    </MenuItem>
                ))}
            </TextField>

            <Box sx={{ mb: 3 }}>
                <TextField
                    select
                    label="Meal Service"
                    value={mealService}
                    onChange={(e) => setMealService(e.target.value)}
                    sx={{ width: 300 }}
                >
                    <MenuItem value="">-- Select --</MenuItem>
                    {["Breakfast", "Lunch", "Supper", "Snack"].map((m) => (
                        <MenuItem key={m} value={m}>
                            {m}
                        </MenuItem>
                    ))}
                </TextField>
            </Box>

            <Typography variant="h3" sx={{ mb: 3, color: "#ADD8E6" }}>
                {student ? `Welcome, ${student.FirstName}` : "Meal Claiming"}
            </Typography>

            {!student ? (
                <>
                    <TextField
                        label="Enter Student ID"
                        value={studentNumber}
                        onChange={(e) => setStudentNumber(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleStudentLookup()}
                        sx={{
                            mb: 2,
                            width: 300,
                            "& .MuiOutlinedInput-root": { borderRadius: 2 },
                        }}
                    />
                    <Box sx={{ mb: 4 }}>
                        {[1, 2, 3].map(n => (
                            <Button key={n} sx={keypadButtonStyle} onClick={() => handleKeypadDigit(n)}>{n}</Button>
                        ))}
                        <br />
                        {[4, 5, 6].map(n => (
                            <Button key={n} sx={keypadButtonStyle} onClick={() => handleKeypadDigit(n)}>{n}</Button>
                        ))}
                        <br />
                        {[7, 8, 9].map(n => (
                            <Button key={n} sx={keypadButtonStyle} onClick={() => handleKeypadDigit(n)}>{n}</Button>
                        ))}
                        <br />
                        <Button sx={keypadButtonStyle} onClick={handleBackspace}>←</Button>
                        <Button sx={keypadButtonStyle} onClick={() => handleKeypadDigit(0)}>0</Button>
                        <Button sx={keypadButtonStyle} onClick={handleClear}>C</Button>
                    </Box>
                    <Button
                        variant="contained"
                        disabled={!selectedDeviceInfo || !mealService}
                        onClick={() => handleStudentLookup()}
                        sx={{ fontSize: 24, p: 2, borderRadius: 2 }}
                    >
                        Claim Meal
                    </Button>
                </>
            ) : (
                <Box sx={{ mb: 4 }}>
                    <img
                        src={student.ImageURL || ""}
                        alt="Student"
                        style={{ width: 100, height: 100, borderRadius: "50%" }}
                    />
                    <Typography variant="h5">{student.FirstName} {student.LastName}</Typography>
                    <Typography>Grade: {student.Grade}</Typography>
                    <Typography>HomeRoom: {student.HomeRoom}</Typography>
                    <Box sx={{ mt: 1, color: "red" }}>
                        {studentAllergens.length
                            ? studentAllergens.map((a, i) => <div key={i}>{a.AllergenName}</div>)
                            : "No known allergens"}
                    </Box>
                </Box>
            )}

            <Modal open={confirmationOpen} onClose={closeConfirmation}>
                <Box sx={modalStyle}>
                    {confirmationData.imageUrl && (
                        <img
                            src={confirmationData.imageUrl}
                            alt="Student"
                            style={{ width: 150, height: 150, borderRadius: "50%" }}
                        />
                    )}
                    <Typography variant="h4" sx={{ mb: 2 }}>Meal Claimed!</Typography>
                    {["studentName", "deviceName", "schoolName", "locationName", "mealService", "time"].map((f) => (
                        <Typography key={f}>
                            {f === "time"
                                ? `Time: ${confirmationData.time?.toLocaleTimeString()}`
                                : `${f.charAt(0).toUpperCase() + f.slice(1)}: ${confirmationData[f] || ""}`}
                        </Typography>
                    ))}
                    <Button
                        variant="contained"
                        onClick={closeConfirmation}
                        sx={{ mt: 3, fontSize: 20, borderRadius: 2 }}
                    >
                        CLOSE
                    </Button>
                </Box>
            </Modal>

            {error && (
                <Typography variant="h5" color="error" sx={{ mt: 2 }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
};

export default StudentMeals;
